import { useEffect, useState } from 'react';
import { Users, Plus, Edit2, Trash2, Save, X, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Password visibility states
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  
  const [newAdmin, setNewAdmin] = useState({
    username: '',
    email: '',
    password: ''
  });

  const [editData, setEditData] = useState({
    new_username: '',
    email: '',
    new_password: ''
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/admins');
      setAdmins(response.data);
    } catch (error) {
      toast.error('خطا در بارگذاری ادمین‌ها');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (newAdmin.password.length < 6) {
      toast.error('رمز عبور باید حداقل 6 کاراکتر باشد');
      return;
    }

    try {
      await api.post('/admins', newAdmin);
      toast.success('ادمین جدید با موفقیت ایجاد شد');
      setNewAdmin({ username: '', email: '', password: '' });
      setIsCreating(false);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطا در ایجاد ادمین');
    }
  };

  const handleUpdate = async (username) => {
    try {
      await api.put(`/admins/${username}`, editData);
      toast.success('اطلاعات ادمین با موفقیت بروزرسانی شد');
      setEditingAdmin(null);
      setEditData({ new_username: '', email: '', new_password: '' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطا در بروزرسانی');
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`آیا مطمئن هستید که می‌خواهید ادمین "${username}" را حذف کنید?`)) {
      return;
    }

    try {
      await api.delete(`/admins/${username}`);
      toast.success('ادمین با موفقیت حذف شد');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطا در حذف ادمین');
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-zinc-400">در حال بارگذاری...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-3">
          <Users className="text-[#D4AF37]" size={32} />
          <h1 className="text-3xl font-light text-white">مدیریت ادمین‌ها</h1>
        </div>
        <button
          onClick={() => setIsCreating(!isCreating)}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#F4D03F] text-black font-medium rounded-lg hover:shadow-lg transition-all"
        >
          {isCreating ? <X size={20} /> : <Plus size={20} />}
          {isCreating ? 'انصراف' : 'افزودن ادمین'}
        </button>
      </div>

      {/* Create New Admin Form */}
      {isCreating && (
        <div className="mb-8 glass-effect border border-[#D4AF37]/30 rounded-xl p-6">
          <h2 className="text-xl font-medium text-white mb-4">ایجاد ادمین جدید</h2>
          <form onSubmit={handleCreate} className="grid md:grid-cols-3 gap-4">
            <input
              type="text"
              name="username"
              placeholder="نام کاربری *"
              required
              value={newAdmin.username}
              onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
              className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
            />
            <input
              type="email"
              name="email"
              placeholder="ایمیل"
              value={newAdmin.email}
              onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
              className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
            />
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                name="password"
                placeholder="رمز عبور *"
                required
                minLength="6"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none pr-12"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-[#D4AF37] transition-colors"
              >
                {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <button
              type="submit"
              className="md:col-span-3 px-6 py-3 bg-[#D4AF37] text-black font-medium rounded-lg hover:bg-[#F4D03F] transition-colors"
            >
              ایجاد ادمین
            </button>
          </form>
        </div>
      )}

      {/* Admins List */}
      <div className="grid gap-4">
        {admins.map((admin) => (
          <div
            key={admin.username}
            className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
          >
            {editingAdmin === admin.username ? (
              // Edit Mode
              <div className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">نام کاربری جدید</label>
                    <input
                      type="text"
                      placeholder={admin.username}
                      value={editData.new_username}
                      onChange={(e) => setEditData({ ...editData, new_username: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">ایمیل</label>
                    <input
                      type="email"
                      placeholder={admin.email || 'ایمیل'}
                      value={editData.email}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">رمز عبور جدید</label>
                    <div className="relative">
                      <input
                        type={showEditPassword ? "text" : "password"}
                        placeholder="رمز عبور جدید (اختیاری)"
                        value={editData.new_password}
                        onChange={(e) => setEditData({ ...editData, new_password: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowEditPassword(!showEditPassword)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-[#D4AF37] transition-colors"
                      >
                        {showEditPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleUpdate(admin.username)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F4D03F] transition-colors"
                  >
                    <Save size={18} />
                    ذخیره
                  </button>
                  <button
                    onClick={() => {
                      setEditingAdmin(null);
                      setEditData({ new_username: '', email: '', new_password: '' });
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <X size={18} />
                    انصراف
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-medium text-white mb-1">{admin.username}</h3>
                  {admin.email && (
                    <p className="text-sm text-zinc-400">{admin.email}</p>
                  )}
                  {admin.created_at && (
                    <p className="text-xs text-zinc-500 mt-1">
                      تاریخ ایجاد: {new Date(admin.created_at).toLocaleDateString('fa-IR')}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingAdmin(admin.username);
                      setEditData({
                        new_username: '',
                        email: admin.email || '',
                        new_password: ''
                      });
                    }}
                    className="p-3 bg-white/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] rounded-lg transition-colors"
                    title="ویرایش"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(admin.username)}
                    className="p-3 bg-white/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                    title="حذف"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {admins.length === 0 && (
        <div className="text-center py-12 text-zinc-400">
          <Users size={48} className="mx-auto mb-4 opacity-50" />
          <p>هیچ ادمینی یافت نشد.</p>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
